<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { getEventEmailSettings, updateEventEmailSettings } from '@/api/eventEmailSettings'
import AppSpinner from '@/components/shared/AppSpinner.vue'

const MAX_LENGTH = 2000

const props = defineProps<{
  eventSlug: string
}>()

const { t } = useI18n()

const loading = ref(false)
const saving = ref(false)
const fetchError = ref(false)
const saveError = ref(false)
const successMessage = ref<string | null>(null)
const errors = ref<Record<string, string[]>>({})

const confirmedMessage = ref<string>('')
const cancelledMessage = ref<string>('')

function isAxiosValidationError(
  e: unknown,
): e is { response: { status: number; data: { errors: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

onMounted(async () => {
  loading.value = true
  fetchError.value = false
  try {
    const settings = await getEventEmailSettings(props.eventSlug)
    confirmedMessage.value = settings.registration_confirmed_message ?? ''
    cancelledMessage.value = settings.registration_cancelled_message ?? ''
  } catch {
    fetchError.value = true
  } finally {
    loading.value = false
  }
})

async function handleSave(): Promise<void> {
  errors.value = {}
  saveError.value = false
  successMessage.value = null
  saving.value = true

  try {
    const updated = await updateEventEmailSettings(props.eventSlug, {
      registration_confirmed_message: confirmedMessage.value !== '' ? confirmedMessage.value : null,
      registration_cancelled_message: cancelledMessage.value !== '' ? cancelledMessage.value : null,
    })
    confirmedMessage.value = updated.registration_confirmed_message ?? ''
    cancelledMessage.value = updated.registration_cancelled_message ?? ''
    successMessage.value = t('eventEmailSettings.saved')
    setTimeout(() => {
      successMessage.value = null
    }, 3000)
  } catch (e: unknown) {
    if (isAxiosValidationError(e) && e.response.status === 422) {
      errors.value = e.response.data.errors
    } else {
      saveError.value = true
    }
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
    <div class="mb-6">
      <h2 class="text-xl font-bold text-white">{{ t('eventEmailSettings.title') }}</h2>
      <p class="text-sm text-slate-400 mt-1">{{ t('eventEmailSettings.description') }}</p>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="flex items-center justify-center py-8">
      <svg class="animate-spin h-6 w-6 text-emerald-500" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    </div>

    <!-- Fetch error state -->
    <div
      v-else-if="fetchError"
      class="rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-4 text-sm text-red-400"
    >
      {{ t('common.error_generic') }}
    </div>

    <template v-else>
      <div class="space-y-6">
        <!-- Registration confirmed message -->
        <div>
          <label
            for="registration-confirmed-message"
            class="block text-sm font-medium text-slate-300 mb-1"
          >
            {{ t('eventEmailSettings.registrationConfirmedLabel') }}
          </label>
          <textarea
            id="registration-confirmed-message"
            v-model="confirmedMessage"
            rows="5"
            :maxlength="MAX_LENGTH"
            :placeholder="t('eventEmailSettings.registrationConfirmedPlaceholder')"
            class="w-full rounded-lg bg-slate-900 border px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y"
            :class="errors['registration_confirmed_message'] ? 'border-red-500' : 'border-slate-600'"
          />
          <div class="mt-1 flex items-start justify-between gap-4">
            <p
              v-if="errors['registration_confirmed_message']?.[0]"
              class="text-xs text-red-400"
            >
              {{ errors['registration_confirmed_message'][0] }}
            </p>
            <span v-else class="flex-1" />
            <span class="text-xs text-slate-500 shrink-0">
              {{ t('eventEmailSettings.charCount', { current: confirmedMessage.length, max: MAX_LENGTH }) }}
            </span>
          </div>
        </div>

        <!-- Registration cancelled message -->
        <div>
          <label
            for="registration-cancelled-message"
            class="block text-sm font-medium text-slate-300 mb-1"
          >
            {{ t('eventEmailSettings.registrationCancelledLabel') }}
          </label>
          <textarea
            id="registration-cancelled-message"
            v-model="cancelledMessage"
            rows="5"
            :maxlength="MAX_LENGTH"
            :placeholder="t('eventEmailSettings.registrationCancelledPlaceholder')"
            class="w-full rounded-lg bg-slate-900 border px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y"
            :class="errors['registration_cancelled_message'] ? 'border-red-500' : 'border-slate-600'"
          />
          <div class="mt-1 flex items-start justify-between gap-4">
            <p
              v-if="errors['registration_cancelled_message']?.[0]"
              class="text-xs text-red-400"
            >
              {{ errors['registration_cancelled_message'][0] }}
            </p>
            <span v-else class="flex-1" />
            <span class="text-xs text-slate-500 shrink-0">
              {{ t('eventEmailSettings.charCount', { current: cancelledMessage.length, max: MAX_LENGTH }) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Generic save error -->
      <div
        v-if="saveError"
        class="mt-4 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
      >
        {{ t('eventEmailSettings.errorGeneric') }}
      </div>

      <!-- Success message -->
      <div
        v-if="successMessage"
        class="mt-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 text-sm text-emerald-400"
      >
        {{ successMessage }}
      </div>

      <!-- Save button -->
      <div class="mt-6 flex justify-end">
        <button
          type="button"
          :disabled="saving || loading"
          class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
          @click="handleSave"
        >
          <AppSpinner v-if="saving" size="button" />
          {{ saving ? t('common.loading') : t('eventEmailSettings.save') }}
        </button>
      </div>
    </template>
  </div>
</template>
